 import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserGroupIcon, ArrowLeftIcon, TrophyIcon, 
  CheckCircleIcon, SparklesIcon, ClipboardIcon,
  PlusIcon, InformationCircleIcon, ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { joinTeam, createTeam, getMyTeam, leaveTeam } from "../../api/teamApi";
import { useToast } from '../../contexts/ToastContext';

const TeamDashboard = () => {
  const showToast = useToast();
  const [view, setView] = useState('landing'); 
  const [formData, setFormData] = useState({ name: '', token: '', teamName: '' });
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true); 
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const data = await getMyTeam();
        if (data.team) {
          setTeamData({
            ...data.team,
            teamName: data.team.team_name || data.team.name,
            teamToken: data.team.team_token || data.team.token,
            members: data.team.members || data.team.team_members || []
          });
        }
      } catch (err) {
        console.error("Error fetching team data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);
  
  const handleCreate = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const data = await createTeam({ team_name: formData.teamName });
      if (data && data.team) {
        setTeamData(data.team); 
        showToast('System Group Created!', 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to create team", 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const data = await joinTeam({ team_token: formData.token });
      if (data.team) {
        setTeamData(data.team);
        showToast('Link Established!', 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Invalid System Token", 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm("Sever connection with this squad?")) return;
    try {
      await leaveTeam();
      setTeamData(null);
      setView('landing');
      showToast('Connection Terminated', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || "Error leaving team", 'error');
    }
  };

  const copyToken = () => {
    if (!teamData?.teamToken) {
      showToast('Token missing', 'error');
      return;
    }
    navigator.clipboard.writeText(teamData.teamToken);
    showToast('Token Securely Copied', 'success');
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500 mx-auto mb-4"></div>
        <p className="text-indigo-400 text-[10px] font-black tracking-[0.3em] uppercase italic">Initializing Hub...</p>
      </div>
    </div>
  );

  const StepGuide = () => (
    <div className="bg-indigo-600/[0.03] border border-indigo-500/10 rounded-[2rem] p-8 mb-12">
      <h3 className="flex items-center gap-3 text-indigo-400 font-black text-xs uppercase tracking-[0.2em] mb-6 italic">
        <InformationCircleIcon className="h-5 w-5" /> Recruitment Protocol
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { s: "01", t: "Initialize", d: "Leader creates the group and generates a unique access signature." },
          { s: "02", t: "Register", d: "Teammates must have active system accounts to join a squad." },
          { s: "03", t: "Link Up", d: "Members input the secure token in the Recruitment Terminal." }
        ].map((step, i) => (
          <div key={i} className="relative p-5 bg-[#0a0a0a] rounded-2xl border border-white/[0.03] group hover:border-indigo-500/30 transition-all">
            <span className="text-4xl font-black text-white/[0.02] absolute top-2 right-4 group-hover:text-indigo-500/5">{step.s}</span>
            <p className="font-black text-white mb-2 text-xs uppercase tracking-widest italic">{step.t}</p>
            <p className="text-[11px] text-gray-600 leading-relaxed font-bold">{step.d}</p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white p-4 md:p-10">
      <AnimatePresence mode="wait">
        {!teamData ? (
          <motion.div 
            key="recruitment"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-5xl mx-auto"
          >
            {view === 'landing' ? (
              <>
                <div className="text-left mb-14">
                  <h1 className="text-5xl md:text-6xl font-black tracking-tighter italic uppercase">
                    Recruitment <span className="text-indigo-600">Hub</span>
                  </h1>
                  <p className="text-gray-600 mt-4 text-sm font-bold uppercase tracking-widest italic">Assemble your elite squad for the next transition.</p>
                </div>
                <StepGuide />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <MenuCard 
                    title="Initialize Team"
                    desc="Take command, generate a secure token, and lead your squad."
                    icon={<PlusIcon className="h-6 w-6" />}
                    color="bg-indigo-600"
                    onClick={() => setView('create')}
                  />
                  <MenuCard 
                    title="Join Squad"
                    desc="Authorized by a leader? Enter your access signature here."
                    icon={<UserGroupIcon className="h-6 w-6" />}
                    color="bg-white/10"
                    onClick={() => setView('join')}
                  />
                </div>
              </>
            ) : (
              <div className="max-w-md mx-auto pt-10">
                <button onClick={() => setView('landing')} className="flex items-center gap-3 text-gray-600 hover:text-indigo-400 mb-8 transition-all font-black text-[10px] uppercase tracking-widest italic group">
                  <ArrowLeftIcon className="h-4 w-4 group-hover:-translate-x-1 transition-transform" /> Back to Terminal
                </button>
                <div className="bg-[#0a0a0a] p-10 rounded-[2.5rem] border border-white/5 shadow-2xl">
                  <h2 className="text-2xl font-black italic uppercase mb-8">{view === 'create' ? 'Team Initialization' : 'Link Connection'}</h2>
                  <form onSubmit={view === 'create' ? handleCreate : handleJoin} className="space-y-6">
                    {view === 'create' ? (
                      <Input placeholder="Designation Name" onChange={(e) => setFormData({...formData, teamName: e.target.value})} />
                    ) : (
                      <>
                        <Input placeholder="Identity Name" onChange={(e) => setFormData({...formData, name: e.target.value})} />
                        <Input placeholder="Access Token (T-XXXXXX)" onChange={(e) => setFormData({...formData, token: e.target.value.toUpperCase()})} isMono />
                      </>
                    )}
                    <button disabled={actionLoading} className="w-full bg-indigo-600 hover:bg-indigo-500 py-5 rounded-2xl font-black text-xs uppercase tracking-[0.2em] italic transition-all shadow-xl shadow-indigo-600/20 disabled:opacity-50">
                      {actionLoading ? "Syncing..." : view === 'create' ? "Generate System" : "Establish Link"}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div 
            key="dashboard"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-6xl mx-auto"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-14">
              <div>
                <h1 className="text-5xl font-black italic uppercase tracking-tighter">{teamData.teamName}</h1>
                <p className="text-indigo-500 font-black text-[10px] uppercase tracking-[0.3em] mt-2">Node Signature: {teamData.teamId? teamData.teamId.toString().slice(0,8):'Scanning...'}</p>
              </div>
              <button onClick={handleLeave} className="flex items-center gap-3 text-red-500 hover:bg-red-500/5 px-6 py-3 rounded-xl transition-all border border-red-500/10 font-black text-[10px] uppercase tracking-widest italic">
                <ArrowRightOnRectangleIcon className="h-4 w-4" /> Sever Connection
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <StatCard label="Squad Strength" value={`${teamData.members?.length || 0} / 4`} icon={<UserGroupIcon className="text-indigo-500" />} />
              <StatCard label="Link Status" value="Online" icon={<CheckCircleIcon className="text-emerald-500" />} subtitle="System Secure" />
              <StatCard label="Division" value="Championship" icon={<TrophyIcon className="text-amber-500" />} subtitle="Qualified" />
            </div>

            <div className="bg-indigo-600 p-10 rounded-[3rem] mb-12 flex flex-col lg:flex-row items-center justify-between shadow-2xl shadow-indigo-600/20 relative overflow-hidden group">
               <div className="relative z-10 text-center lg:text-left mb-8 lg:mb-0">
                  <h3 className="text-3xl font-black italic uppercase tracking-tighter mb-2">Recruit Operatives</h3>
                  <p className="text-indigo-100 font-bold text-sm uppercase tracking-tighter opacity-80">Distribute this secure signature to establish member links.</p>
               </div>
               <div className="flex items-center gap-4 bg-black/30 p-4 rounded-3xl border border-white/10 relative z-10 backdrop-blur-xl">
                  <code className="text-3xl font-black px-6 tracking-[0.2em] text-white">{teamData.teamToken}</code>
                  <button onClick={copyToken} className="p-5 bg-white text-indigo-600 rounded-2xl hover:scale-105 active:scale-95 transition-all shadow-2xl">
                    <ClipboardIcon className="h-7 w-7" />
                  </button>
               </div>
               <SparklesIcon className="absolute -right-6 -bottom-6 h-48 w-48 opacity-10 rotate-12 group-hover:rotate-45 transition-transform duration-1000" />
            </div>

            <div className="bg-[#0a0a0a] rounded-[2.5rem] border border-white/5 overflow-hidden shadow-3xl">
               <div className="p-8 border-b border-white/5 bg-white/[0.01]">
                 <h3 className="font-black text-xs uppercase tracking-[0.2em] italic flex items-center gap-3 text-gray-400">
                    <UserGroupIcon className="h-5 w-5 text-indigo-500" /> Authorized Operatives
                 </h3>
               </div>
               <div className="divide-y divide-white/[0.02]">
                  {teamData.members?.map((member, i) => (
                    <div key={i} className="p-7 flex items-center justify-between hover:bg-white/[0.01] transition-colors">
                      <div className="flex items-center gap-5">
                        <div className="h-14 w-14 bg-gradient-to-tr from-indigo-600 to-indigo-900 rounded-2xl flex items-center justify-center font-black text-xl italic shadow-inner">
                          {member.name?.[0] || 'M'}
                        </div>
                        <div>
                          <p className="font-black text-lg italic uppercase tracking-tight">{member.name}</p>
                          <p className="text-[9px] text-gray-700 uppercase font-black tracking-[0.2em] mt-1">{member.role}</p>
                        </div>
                      </div>
                      {member.role === 'LEADER' && (
                        <span className="bg-indigo-500/5 text-indigo-400 text-[9px] px-3 py-1.5 rounded-lg border border-indigo-500/10 font-black uppercase tracking-widest italic">
                          Captain
                        </span>
                      )}
                    </div>
                  ))}
               </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MenuCard = ({ title, desc, icon, color, onClick }) => (
  <button onClick={onClick} className="group p-10 bg-[#0a0a0a] border border-white/5 rounded-[2.5rem] hover:border-indigo-500/40 transition-all text-left relative overflow-hidden shadow-2xl">
    <div className={`h-16 w-16 ${color} rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform shadow-2xl shadow-indigo-600/10`}>
      {icon}
    </div>
    <h3 className="text-2xl font-black italic uppercase tracking-tighter mb-3">{title}</h3>
    <p className="text-gray-600 text-sm font-bold leading-relaxed">{desc}</p>
    <div className="absolute -right-6 -bottom-6 text-white opacity-[0.02] group-hover:opacity-[0.05] transition-opacity">
       <div className="h-32 w-32">{icon}</div>
    </div>
  </button>
);

const StatCard = ({ label, value, icon, subtitle }) => (
  <div className="bg-[#0a0a0a] p-8 rounded-[2.5rem] border border-white/5 flex flex-col justify-between shadow-xl">
    <div className="flex justify-between items-start mb-6">
      <p className="text-gray-700 text-[10px] font-black uppercase tracking-[0.2em] italic">{label}</p>
      <div className="h-8 w-8 opacity-80">{icon}</div>
    </div>
    <div>
      <p className="text-4xl font-black tracking-tighter italic uppercase">{value}</p>
      {subtitle && <p className="text-[10px] text-indigo-500/60 font-black uppercase tracking-widest mt-2 italic">{subtitle}</p>}
    </div>
  </div>
);

const Input = ({ placeholder, onChange, isMono }) => (
  <input 
    className={`w-full bg-[#050505] border border-white/5 rounded-2xl px-6 py-5 text-white placeholder:text-gray-800 focus:ring-1 focus:ring-indigo-500/50 outline-none transition-all font-bold text-sm ${isMono ? 'font-mono tracking-[0.3em] uppercase' : ''}`}
    placeholder={placeholder}
    onChange={onChange}
    required
  />
);

export default TeamDashboard;